import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { getUserRole } from '@/utils/auth.js';
import { useSessionGuard } from '@/hooks/useSessionGuard.js';
import { useTranslation } from '@/components/i18n/TranslationProvider.jsx';

const DefaultAccessDenied = ({ message }) => {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-[40vh] items-center justify-center">
      <div className="rounded-xl border border-red-500/40 bg-red-500/10 px-6 py-5 text-center text-red-100 shadow-lg">
        <h1 className="text-lg font-semibold uppercase tracking-wide">
          {message || t('auth.protected_route.access_denied_title')}
        </h1>
        <p className="mt-2 text-sm text-red-200/80">
          {t('auth.protected_route.access_denied_description')}
        </p>
      </div>
    </div>
  );
};

const DefaultLoadingState = () => {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center">
      <div className="rounded-xl border border-primary/40 bg-primary/10 px-6 py-5 text-center text-primary-100 shadow-lg">
        <h1 className="text-base font-medium uppercase tracking-wide">
          {t('auth.protected_route.loading_title')}
        </h1>
        <p className="mt-2 text-sm text-primary-100/80">
          {t('auth.protected_route.loading_description')}
        </p>
      </div>
    </div>
  );
};

const SessionErrorState = ({ message }) => {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center">
      <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-6 py-5 text-center text-amber-100 shadow-lg">
        <h1 className="text-base font-semibold uppercase tracking-wide">
          {message || t('auth.protected_route.session_error_title')}
        </h1>
        <p className="mt-2 text-sm text-amber-100/80">
          {t('auth.protected_route.session_error_description')}
        </p>
      </div>
    </div>
  );
};

const normalizeList = (value) => {
  if (!value) {
    return null;
  }

  return Array.isArray(value) ? value : [value];
};

const ProtectedRoute = ({
  children,
  requiredRoles = null,
  requiredPermissions = null,
  redirectTo = '/login',
  loadingFallback = <DefaultLoadingState />,
  deniedFallback = null,
  sessionErrorFallback = <SessionErrorState />,
}) => {
  const { hasSession, isChecking, session, user, permissions, error } = useSessionGuard();
  const location = useLocation();

  if (isChecking) {
    return loadingFallback;
  }

  if (!hasSession) {
    if (error) {
      console.warn('[ProtectedRoute] session guard rejected the current session');
      return sessionErrorFallback;
    }
    return <Navigate to={redirectTo} replace state={{ from: location }} />;
  }

  const normalizedRoles = normalizeList(requiredRoles);
  const normalizedPermissions = normalizeList(requiredPermissions);

  const userRole = getUserRole(user || session?.user);
  const userPermissions = Array.isArray(permissions) ? permissions : [];

  if (normalizedRoles && !normalizedRoles.includes(userRole)) {
    if (deniedFallback) {
      return deniedFallback;
    }

    return <DefaultAccessDenied />;
  }

  if (normalizedPermissions) {
    const hasRequiredPermissions =
      userRole === 'admin' || normalizedPermissions.every((perm) => userPermissions.includes(perm));

    if (!hasRequiredPermissions) {
      if (deniedFallback) {
        return deniedFallback;
      }

      return <DefaultAccessDenied />;
    }
  }

  return children;
};

export default ProtectedRoute;
